import React, { useState } from 'react';
import { Button, Popconfirm, Space, Table, TableProps, Tag } from 'antd';
import { CanAccess, useDelete } from '@refinedev/core';
import { DeleteOutlined } from '@ant-design/icons';
import { IClassroom } from 'interfaces/classrooms';
import { IGraduateCourse } from 'interfaces/graduate_courses';
import { INoticeBoard } from 'interfaces/notice_board';
import { ShowButton, TextField } from '@refinedev/antd';
import { formatDate } from 'services/date';
import { translateKind, translatePriority } from '../list';

interface ListTableProps {
  tableProps: TableProps<INoticeBoard>;
}

export const ListTable = ({ tableProps }: ListTableProps) => {
  const { mutate: deleteNoticeBoard } = useDelete();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = (id: string) => {
    setDeletingId(id);

    deleteNoticeBoard(
      {
        resource: 'notice_boards',
        id,
        successNotification: {
          type: 'success',
          description: 'Sucesso!',
          message: 'Aviso excluído com sucesso'
        },
        errorNotification: {
          type: 'error',
          description: 'Erro!',
          message: 'Não foi possível excluir o aviso'
        }
      },
      {
        onSettled: () => {
          setDeletingId(null);
        }
      }
    );
  };

  return (
    <Table {...tableProps} rowKey="id">
      <Table.Column
        dataIndex="title"
        key="title"
        title="Título"
        render={value => <TextField value={value} />}
      />
      <Table.Column
        key="target"
        title="Destino"
        className="max-w-[500px]"
        render={value => (
          <Space direction="vertical" size="small" className="w-full">
            <Tag color="#f0f0f0" style={{ color: 'black' }}>
              {translateKind(value.kind)}
            </Tag>
            {value.kind === 'classroom' && (
              <TextField
                ellipsis={true}
                value={value.classrooms
                  .map(
                    (classroom: IClassroom) =>
                      `${classroom.discipline.name} - ${classroom.code}`
                  )
                  .join(', ')
                  .slice(0, 100)}
              />
            )}
            {value.kind === 'graduate_course' && (
              <TextField
                ellipsis={true}
                value={value.graduate_courses
                  .map((course: IGraduateCourse) => `${course.name}`)
                  .join(', ')
                  .slice(0, 100)}
              />
            )}
          </Space>
        )}
      />
      <Table.Column
        dataIndex="created_at"
        key="created_at"
        title="Data de criação"
        render={value => <TextField value={formatDate(value, 'LL')} />}
      />
      <Table.Column
        dataIndex="starts_at"
        key="starts_at"
        title="Data de publicação"
        render={value => <TextField value={formatDate(value)} />}
      />
      <Table.Column
        dataIndex="priority"
        key="priority"
        title="Prioridade"
        render={value => (
          <>
            {value === 'regular' && (
              <Tag color="#f0f0f0" style={{ color: 'black' }}>
                {translatePriority(value)}
              </Tag>
            )}
            {value === 'high' && (
              <Tag color="red">{translatePriority(value)}</Tag>
            )}
          </>
        )}
      />
      <Table.Column<INoticeBoard>
        title="Ações"
        align="center"
        dataIndex="actions"
        render={(_, record) => {
          return (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} />

              <CanAccess resource="notice_boards" action="destroy">
                <Popconfirm
                  key="delete"
                  title="Tem certeza?"
                  okText="Deletar"
                  cancelText="Cancelar"
                  okType="danger"
                  onConfirm={() => handleDelete(record.id)}
                  okButtonProps={{
                    disabled: deletingId === record.id
                  }}
                >
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={deletingId === record.id}
                  />
                </Popconfirm>
              </CanAccess>
            </Space>
          );
        }}
      />
    </Table>
  );
};
